import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'design-system/styles';
/* eslint-disable restrict-imports/restrict-imports */
import Menu from '@material-ui/core/Menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import Combokeys from 'combokeys';

const INCREMENT_THROTTLE_MS = 1000;
const SEARCH_AREA_ID = 'searcharea';
export interface DropdownInfo {
  options: string[];
  cursorPos: { x: number; y: number };
  followUpCharacter?: string;
}

interface TranscriptionTextDropdownProps {
  dropdownInfo: DropdownInfo;
  setDropdownInfo: (dropdownInfo: DropdownInfo | undefined) => void;
  openTagDropdown: boolean;
  setOpenTagDropdown: (openTagDropdown: boolean) => void;
  handleAutocomplete: (inputs: string[], followUpCharacter: string) => void;
  handleDelete: () => void;
  spellcheck?: boolean;
}

export default function TranscriptionTextDropdown({
  dropdownInfo,
  setDropdownInfo,
  openTagDropdown,
  setOpenTagDropdown,
  handleAutocomplete,
  handleDelete,
  spellcheck,
}: TranscriptionTextDropdownProps) {
  const classes = useStyles();

  const { options, cursorPos, followUpCharacter } = dropdownInfo;
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const [searchInput, setSearchInput] = React.useState<string | undefined>(undefined);
  const [displayedOptions, setDisplayedOptions] = React.useState<string[]>(options);
  const [selectedOptions, setSelectedOptions] = React.useState<string[]>([]);
  const OFFSET = options.length > 1 ? 2 : 0; // num menu items that are not option

  const handleClose = React.useCallback(() => {
    setActiveIndex(0);
    setSearchInput(undefined);
    setOpenTagDropdown(false);
    setDropdownInfo(undefined);
    setDisplayedOptions([]);
    setSelectedOptions([]);
  }, [
    setActiveIndex,
    setSearchInput,
    setOpenTagDropdown,
    setDropdownInfo,
    setDisplayedOptions,
    setSelectedOptions,
  ]);

  const handleSubmit = React.useCallback(() => {
    const origSelectedOptions = [...selectedOptions];
    handleClose();
    // need to close the dropdown before editing the editor state
    if (origSelectedOptions.length > 0) {
      handleAutocomplete(origSelectedOptions, followUpCharacter ?? '');
    }
  }, [selectedOptions, followUpCharacter, handleAutocomplete, handleClose]);

  const toggleOptionSelect = React.useCallback(
    (text: string) => {
      setSelectedOptions(curSelectedOptions =>
        curSelectedOptions.includes(text)
          ? _.uniq(_.compact(_.filter(curSelectedOptions, option => option !== text)))
          : _.uniq(_.compact([...curSelectedOptions, text])),
      );
    },
    [setSelectedOptions],
  );

  const handleClick = React.useCallback(
    (text: string) => {
      if (options.length === 1) {
        handleClose();
        handleAutocomplete(options, followUpCharacter ?? '');
      } else {
        toggleOptionSelect(text);
      }
    },
    [options, followUpCharacter, handleClose, handleAutocomplete, toggleOptionSelect],
  );

  const incrementActiveIndex = React.useCallback(
    (delta: number) => {
      setActiveIndex(curIndex => {
        if (_.isNil(curIndex) || _.isNil(options)) {
          return 0;
        }
        return _.clamp(curIndex + delta, 0, displayedOptions.length + OFFSET);
      });
    },
    [options, setActiveIndex, displayedOptions, OFFSET],
  );

  const handleSearch = React.useCallback(
    (e: KeyboardEvent) => {
      if (_.isNil(searchInput) && e.key === 'Backspace') {
        e.preventDefault();
        handleClose();
        // need to close the dropdown before editing the editor state
        handleDelete();
      } else if (!_.isNil(searchInput) && searchInput.length === 0 && e.key === 'Backspace') {
        setSearchInput(undefined);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        incrementActiveIndex(-1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        incrementActiveIndex(1);
      }
    },
    [searchInput, handleClose, handleDelete, setSearchInput, incrementActiveIndex],
  );

  React.useEffect(() => {
    const searchAreaElem = document.getElementById(SEARCH_AREA_ID);
    if (!searchAreaElem) {
      return;
    }
    if (activeIndex === 0) {
      searchAreaElem.focus();
    } else {
      searchAreaElem.blur();
    }
  }, [activeIndex]);

  React.useEffect(() => {
    const searchAreaElem = document.getElementById(SEARCH_AREA_ID);
    if (!searchAreaElem) {
      return;
    }
    // NOTE: don't think there's an easy way to use combokeys with inputs?
    // https://github.com/avocode/combokeys/issues/7
    searchAreaElem.addEventListener('keydown', handleSearch);
    return () => {
      searchAreaElem.removeEventListener('keydown', handleSearch);
    };
  }, [handleSearch]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const combokeys = new Combokeys(document.body);

    combokeys.bind(
      ['down'],
      _.throttle(() => {
        incrementActiveIndex(1);
        return false;
      }, INCREMENT_THROTTLE_MS),
      'keydown',
    );

    combokeys.bind(
      ['up'],
      _.throttle(() => {
        incrementActiveIndex(-1);
        return false;
      }, INCREMENT_THROTTLE_MS),
      'keydown',
    );

    combokeys.bind(['esc'], () => {
      handleClose();
      return false;
    });

    if (options.length === 1) {
      combokeys.bind(['tab'], () => {
        // just one item so just autocomplete
        handleClose();
        handleAutocomplete(options, followUpCharacter ?? '');
        return false;
      });
      combokeys.bind(['backspace', 'del'], () => {
        handleClose();
        // need to close the dropdown before editing the editor state
        handleDelete();
        return false;
      });
    } else if (activeIndex >= OFFSET) {
      combokeys.bind(['tab'], () => {
        handleClick(displayedOptions[activeIndex - OFFSET]);
        return false;
      });
    }

    combokeys.bind(['enter'], () => {
      handleSubmit();
      return false;
    });

    return () => {
      combokeys.detach();
    };
  }, [
    activeIndex,
    OFFSET,
    displayedOptions,
    selectedOptions,
    followUpCharacter,
    options,
    toggleOptionSelect,
    incrementActiveIndex,
    handleClose,
    handleAutocomplete,
    handleDelete,
    handleClick,
    handleSubmit,
  ]);

  React.useEffect(() => {
    setDisplayedOptions(() => {
      if (searchInput && searchInput.length > 0) {
        return options.filter(option => option.toLowerCase().includes(searchInput.toLowerCase()));
      }
      return options;
    });
  }, [searchInput, setDisplayedOptions, options]);

  if (!options || !openTagDropdown || options.length === 0) {
    return null;
  }
  return (
    <div>
      <Menu
        keepMounted
        open={openTagDropdown}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: cursorPos.y, left: cursorPos.x }}
      >
        {options.length > 1 && (
          <>
            <div className={classes.horizontal}>
              <h5 className={classes.header}>SEARCH</h5>
              <div onClick={handleClose}>
                <FontAwesomeIcon className={classes.closeButton} icon={'times-circle'} />
              </div>
            </div>

            <textarea
              id={SEARCH_AREA_ID}
              className={classnames(classes.itemArea, { [classes.active]: activeIndex === 0 })}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={spellcheck}
              draggable={false}
              autoFocus
              value={searchInput}
              onMouseEnter={() => setActiveIndex(0)}
              onChange={e => setSearchInput(e.target.value)}
              onClick={e => e.preventDefault()}
            />
          </>
        )}
        {options.length > 1 && (
          <>
            <h5 className={classes.header}>SELECTED</h5>
            <div
              className={classnames(classes.item, classes.selected, {
                [classes.active]: activeIndex === 1,
              })}
            >{`${selectedOptions.length === 0 ? 'None' : selectedOptions.join(', ')}`}</div>
          </>
        )}
        <h5 className={classes.header}>OPTIONS</h5>
        {_.map(displayedOptions, (option, index) => (
          <div
            className={classnames(classes.item, {
              [classes.active]: activeIndex === index + OFFSET,
            })}
            key={index}
            onClick={() => handleClick(option)}
            onMouseEnter={() => setActiveIndex(index + OFFSET)}
          >
            <div>{option}</div>
            {selectedOptions.includes(option) && <FontAwesomeIcon icon={'check'} />}
          </div>
        ))}
        {options.length > 1 && (
          <div className={classes.alignRight}>
            <button className={classes.submitButton} onClick={handleSubmit}>
              Submit
            </button>
          </div>
        )}
      </Menu>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  horizontal: {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
  },
  closeButton: {
    color: 'rgba(55, 53, 47, 0.6)',
    marginRight: 5,
    cursor: 'pointer',
  },
  item: {
    'flex-direction': 'row',
    display: 'flex',
    'align-items': 'center',
    padding: '4px 6px',
    'font-size': '14px',
    'line-height': '20px',
    'border-radius': '3px',
    cursor: 'pointer',
    'min-width': '200px',
    marginLeft: 5,
    marginRight: 5,
    justifyContent: 'space-between',
  },
  itemArea: {
    display: 'flex',
    'align-items': 'center',
    padding: '4px 6px',
    'line-height': '20px',
    'border-radius': '3px',
    cursor: 'pointer',
    'font-family': theme.typography.fontFamily,
    'font-size': '14px',
    'min-width': '200px',
    marginLeft: 5,
    marginRight: 5,
    marginBottom: 10,
    border: 'none',
    'box-shadow': '0 5px 15px rgba(0, 0, 0, 0.1)',
  },
  active: {
    background: '#f3ecfd',
  },
  selected: {
    color: 'rgba(55, 53, 47, 0.6)',
    fontSize: 11,
    marginLeft: 7,
  },
  header: {
    margin: '6px',
    color: 'rgba(55, 53, 47, 0.6)',
    'font-size': '11px',
    'font-weight': 500,
    'user-select': 'none',
    marginLeft: 7,
  },
  alignRight: { float: 'right' },
  submitButton: {
    marginLeft: 7,
    cursor: 'pointer',
    border: 'none',
    'box-shadow': '0 5px 15px rgba(0, 0, 0, 0.1)',
    marginRight: 5,
    marginBottom: 5,
    backgroundColor: 'rgba(55, 53, 47, 0.4)',
    'border-radius': '3px',
  },
}));
